<template>
    <div
        class="modal fade"
        v-bind:class="{ right: sidePanel && !left, left: sidePanel && left }">
        <div
            v-bind:class="modalClass"
            class="modal-dialog">
            <div
                v-bind:class="modalContentClass"
                class="modal-content">
                <div
                    v-if="showHeader"
                    ref="header"
                    class="modal-header">
                    <slot name="header" />
                </div>
                <div
                    v-bind:style="modalBodyStyle"
                    v-bind:class="modalBodyClass"
                    class="modal-body">
                    <slot />
                </div>
                <div
                    v-if="showFooter"
                    ref="footer"
                    class="modal-footer">
                    <slot name="footer" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { isIE } from "Util/util.js";
    import { Modal } from "bootstrap";

    export default {
        props: {
            showFooter: { type: Boolean, required: false, default: true },
            showHeader: { type: Boolean, required: false, default: true },
            center: { type: Boolean, required: false, default: true },
            small: { type: Boolean, required: false, default: true },
            large: { type: Boolean, required: false, default: false },
            extraLarge: { type: Boolean, required: false, default: false },
            sidePanel: { type: Boolean, required: false, default: false },
            left: { type: Boolean, required: false, default: false },
            fitContent: { type: Boolean, required: false, default: false },
            dismissOnOutsideClick: {
                type: Boolean,
                required: false,
                default: false
            },
            hideBodyPadding: { type: Boolean, required: false, default: false },
            scrollable: { type: Boolean, required: false, default: true }
        },
        emits: ["hidden", "shown"],
        data() {
            return { headerSize: 0, footerSize: 0 };
        },
        computed: {
            modalClass() {
                return {
                    //When sidePanel then modal-dialog-centered should not be applied
                    //When sidePanel, width is auto created based on content.
                    "modal-dialog-centered": this.center && !this.sidePanel,
                    "modal-sm": this.small && !this.large && !this.extraLarge && !this.sidePanel,
                    "modal-lg": this.large && !this.extraLarge && !this.sidePanel,
                    "modal-xl": this.extraLarge && !this.sidePanel,
                    "modal-dialog-scrollable": this.scrollable,
                    // this fixes the issue of when viewport is XS
                    // else gives weird behavior of scrolling content on phone
                    "w-100": isIE() || this.scrollable
                };
            },
            modalContentClass() {
                return {
                    "h-100": this.sidePanel,
                    "border-0": this.sidePanel,
                    "rounded-0": this.sidePanel,
                    "modal-fit-content": this.fitContent
                };
            },
            modalBodyClass() {
                return {
                    "p-0": this.hideBodyPadding,
                    "h-100": isIE()
                };
            },
            modalBodyStyle() {
                // Add one px to account for rounding errors causing scrollbars to appear
                const marginSize = this.headerSize + this.footerSize - 1;

                return {
                    "max-height": "calc(100% - " + marginSize + "px)"
                };
            }
        },
        mounted() {
            // store the object that bootstrap 5 uses to manage modal.
            this.modalWidget = new Modal(this.$el, {
                backdrop: "static",
                keyboard: false,
                show: false
            });
            this.$el.addEventListener("hidden.bs.modal", () => {
                this.$emit("hidden");
            });
            this.$el.addEventListener("shown.bs.modal", () => {
                this.$emit("shown");
            });
            this.$el.addEventListener("hidePrevented.bs.modal", this.outsideClick);
        },
        unmounted() {
            this.modalWidget.dispose();
        },
        methods: {
            show() {
                this.headerSize = 0;
                this.footerSize = 0;
                this.modalWidget.show();
                this.$nextTick(function () {
                    this.setHeaderFooterSize();
                });
            },
            hide() {
                this.modalWidget.hide();
            },
            async outsideClick() {
                if (this.dismissOnOutsideClick) {
                    await this.$nextTick();
                    this.hide();
                }
            },
            setHeaderFooterSize() {
                this.headerSize = this.showHeader ? this.$refs.header.offsetHeight : 0;
                this.footerSize = this.showFooter ? this.$refs.footer.offsetHeight : 0;
                if ((this.showHeader && this.headerSize === 0) || (this.showFooter && this.footerSize === 0)) {
                    window.requestAnimationFrame(this.setHeaderFooterSize);
                }
            }
        }
    };
</script>

<style lang="scss">
    .modal.right .modal-dialog,
    .modal.left .modal-dialog {
        position: fixed;
        margin: 0;
        height: 100%;
        max-height: 100%;
        transform: translate3d(0%, 0, 0);
    }
    .modal.right.fade.show .modal-dialog {
        right: 0;
    }
    .modal.left.fade.show .modal-dialog {
        left: 0;
    }
    .modal.right.fade .modal-dialog {
        right: -100%;
        transition: all 0.4s ease;
    }
    .modal.left.fade .modal-dialog {
        left: -100%;
        transition: all 0.4s ease;
    }
    .modal-dialog-scrollable .modal-content {
        max-height: 100% !important;

        // This is necessary in IE, otherwise if the contents expand
        // beyond their container, they will not be visible and may
        // block the dialog buttons
        overflow-y: auto;
    }
    .modal-fit-content {
        width: max-content;
    }
</style>
